import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity, ArrowDownRight, ArrowRight, ArrowUpRight, ChevronLeft, ChevronRight,
  Minus, Plus, Search,
} from "lucide-react";

import { IndicatorFormDialog } from "@/components/IndicatorFormDialog";
import { ImportCsvDialog } from "@/components/ImportCsvDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useCategories, useIndicators } from "@/hooks/useIndicators";
import { formatDate } from "@/lib/format";
import { formatPeriod } from "@/lib/indicator-period";
import { cn } from "@/lib/utils";
import type { IndicatorDirection, IndicatorRow } from "@/lib/types";

/** /indicators — list of every Indicator the actor can see.
 *  CEO+ gets the "New indicator" button; everyone else gets read-only. */
export default function Indicators() {
  const { data: user } = useCurrentUser();
  const [search,    setSearch]    = useState("");
  const [category,  setCategory]  = useState("");
  const [page,      setPage]      = useState(1);
  const [opening,   setOpening]   = useState(false);
  const [importing, setImporting] = useState(false);

  const list       = useIndicators({
    search:   search || undefined,
    category: category || undefined,
    page,
  });
  const categories = useCategories();

  const canCreate = !!user?.is_ceo_level;
  const data = list.data;
  const pageCount = data ? Math.max(1, Math.ceil(data.count / 20)) : 1;

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">
            <Activity className="h-3.5 w-3.5" />
            Indicators
          </div>
          <h1 className="mt-1 text-3xl font-bold tracking-tight">Performance indicators</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            CEO-authored metrics tracked over time. Set a target, log values
            per period, watch the trend.
          </p>
        </div>
        {canCreate && (
          <div className="flex gap-2 self-start sm:self-auto">
            <Button variant="outline" onClick={() => setImporting(true)}>
              Import CSV
            </Button>
            <Button variant="outline" asChild>
              <a href="/api/v1/indicators/export.csv" download>Export CSV</a>
            </Button>
            <Button onClick={() => setOpening(true)}>
              <Plus className="mr-2 h-4 w-4" />New indicator
            </Button>
          </div>
        )}
      </header>

      <Card className="border-border/60 shadow-soft">
        <CardContent className="p-0">
          <div className="flex flex-wrap items-center gap-3 border-b border-border/60 p-4">
            <div className="relative min-w-[14rem] flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by name…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="h-9 pl-9 pr-3"
              />
            </div>
            <select
              value={category}
              onChange={(e) => { setCategory(e.target.value); setPage(1); }}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm font-medium"
              disabled={categories.isLoading}
            >
              <option value="">All categories</option>
              {(categories.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <span className="ml-auto text-xs text-muted-foreground">
              {data?.count ?? 0} indicator{data?.count === 1 ? "" : "s"}
            </span>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="pl-5">Indicator</TableHead>
                <TableHead className="hidden md:table-cell">Latest value</TableHead>
                <TableHead className="hidden lg:table-cell">Observations</TableHead>
                <TableHead className="hidden sm:table-cell">Visibility</TableHead>
                <TableHead className="hidden xl:table-cell">Updated</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={5} className="py-4">
                      <Skeleton className="h-10 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : data && data.results.length > 0 ? (
                data.results.map((ind) => (
                  <IndicatorTableRow key={ind.id} indicator={ind} />
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="py-16">
                    <div className="text-center">
                      <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                        <Activity className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <h3 className="text-sm font-semibold">
                        {search ? "No matches" : "No indicators yet"}
                      </h3>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {canCreate
                          ? "Click “New indicator” to add the first one."
                          : "A CEO will publish them here once they're set up."}
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {pageCount > 1 && (
            <div className="flex items-center justify-between gap-4 border-t border-border/60 p-3">
              <span className="text-xs text-muted-foreground">
                Page {page} / {pageCount}
              </span>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" disabled={!data?.previous}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  <ChevronLeft className="mr-1 h-3.5 w-3.5" />Prev
                </Button>
                <Button variant="ghost" size="sm" disabled={!data?.next}
                  onClick={() => setPage((p) => p + 1)}>
                  Next<ChevronRight className="ml-1 h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <IndicatorFormDialog open={opening} onOpenChange={setOpening} />

      <ImportCsvDialog
        open={importing}
        onOpenChange={setImporting}
        title="Import indicators from CSV"
        description="Bulk-create or update indicator definitions. Same column shape as the export."
        endpoint="/indicators/import/"
        invalidateKeys={[["indicators"]]}
        columnsHint={
          <>
            <div className="font-semibold mb-1">Recognised columns</div>
            <code className="block font-mono text-[11px]">
              name, unit, period, direction, category, description, visibility
            </code>
            <ul className="mt-2 list-disc pl-4 text-muted-foreground">
              <li><strong>name</strong> is the only required column.</li>
              <li><strong>period</strong> = annual / quarterly / monthly / daily (default quarterly).</li>
              <li><strong>direction</strong> = increasing / decreasing / stable (default increasing).</li>
              <li><strong>category</strong> is required and accepts one OR several names separated by <code>;</code> (e.g. <code>Environmental;Economic</code>). Every name must match an existing category — unknown ones skip the row.</li>
              <li><strong>visibility</strong> = private / staff / public (default staff).</li>
              <li>Rows with a name that matches an existing indicator <strong>update</strong> it; new names <strong>create</strong>.</li>
            </ul>
          </>
        }
      />
    </div>
  );
}

function IndicatorTableRow({ indicator: ind }: { indicator: IndicatorRow }) {
  return (
    <TableRow className="border-border/60">
      <TableCell className="pl-5 py-3">
        <Link
          to={`/indicators/${ind.id}`}
          className="group flex items-center gap-3 min-w-0"
        >
          <DirectionChip direction={ind.direction} />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="truncate text-sm font-medium group-hover:text-primary group-hover:underline">
                {ind.name}
              </span>
              {/* Multi-category — render one chip per name, cap at 3 in
                  the row and show "+N more" if the indicator carries
                  many themes. */}
              {ind.category_names.slice(0, 3).map((name) => (
                <span
                  key={name}
                  className="shrink-0 rounded-full bg-primary/10 px-1.5 py-px text-[10px] font-semibold text-primary"
                  title={`Category: ${name}`}
                >
                  {name}
                </span>
              ))}
              {ind.category_names.length > 3 && (
                <span className="shrink-0 rounded-full bg-muted px-1.5 py-px text-[10px] font-semibold text-muted-foreground"
                  title={ind.category_names.slice(3).join(", ")}>
                  +{ind.category_names.length - 3}
                </span>
              )}
            </div>
            <div className="text-xs text-muted-foreground">
              {ind.period_display} · {ind.direction_display}
              {ind.unit ? ` · ${ind.unit}` : ""}
            </div>
          </div>
        </Link>
      </TableCell>

      <TableCell className="hidden md:table-cell">
        {ind.latest_value != null ? (
          <div className="text-sm font-mono tabular-nums">
            {prettyValue(ind.latest_value)}
            {ind.unit && <span className="ml-1 text-muted-foreground">{ind.unit}</span>}
            {ind.latest_date && (
              <div className="text-xs font-sans text-muted-foreground">
                as of {formatPeriod(ind.latest_date, ind.period)}
              </div>
            )}
          </div>
        ) : (
          <span className="text-xs italic text-muted-foreground">No values yet</span>
        )}
      </TableCell>

      <TableCell className="hidden lg:table-cell text-sm font-mono tabular-nums text-muted-foreground">
        {ind.value_count}
      </TableCell>

      <TableCell className="hidden sm:table-cell">
        <VisibilityBadge state={ind.workflow_state} label={ind.workflow_state_display} />
      </TableCell>

      <TableCell className="hidden xl:table-cell text-sm text-muted-foreground">
        {formatDate(ind.updated_at)}
      </TableCell>
    </TableRow>
  );
}

function DirectionChip({ direction }: { direction: IndicatorDirection }) {
  const map = {
    increasing: { Icon: ArrowUpRight,   cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300" },
    decreasing: { Icon: ArrowDownRight, cls: "bg-rose-100    text-rose-700    dark:bg-rose-500/20    dark:text-rose-300" },
    stable:     { Icon: Minus,          cls: "bg-slate-100   text-slate-700   dark:bg-slate-500/20   dark:text-slate-300" },
  } as const;
  const { Icon, cls } = map[direction] ?? map.increasing;
  return (
    <div className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-lg", cls)}>
      <Icon className="h-4 w-4" />
    </div>
  );
}

function VisibilityBadge({ state, label }: { state: string; label: string }) {
  const cls =
    state === "published"          ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/30"
  : state === "private_only"       ? "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/15 dark:text-blue-300 dark:border-blue-500/30"
  : state === "owner_only"         ? "bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-500/15 dark:text-slate-300 dark:border-slate-500/30"
                                   : "bg-muted/40 text-foreground/80 border-border";
  return <Badge variant="outline" className={cn("border font-medium", cls)}>{label}</Badge>;
}

function prettyValue(raw: string): string {
  // Trim trailing zeros — "42.5000" → "42.5"
  if (!raw.includes(".")) return raw;
  return raw.replace(/0+$/, "").replace(/\.$/, "");
}

// Re-export so the detail page can reuse the same components.
export { DirectionChip, VisibilityBadge, prettyValue };
// Kept around but currently unused; satisfies the linter on the
// unused-imports check until we wire reorder controls.
void ArrowRight;
